import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brain, Target, Users, Zap, Globe, Heart, Check, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useParallax } from '@/hooks/useParallax';
import DocumentTitle from '@/components/DocumentTitle';

const Pricing = () => {
  const { user } = useAuth();
  
  // Refs for parallax elements
  const starsRef = useRef<HTMLDivElement>(null);
  const planet1Ref = useRef<HTMLDivElement>(null);
  const planet2Ref = useRef<HTMLDivElement>(null);
  
  // Parallax styles
  const starsStyle = useParallax(starsRef, { speed: 0.05 });
  const planet1Style = useParallax(planet1Ref, { speed: -0.1 });
  const planet2Style = useParallax(planet2Ref, { speed: -0.15 });

  const plans = [
    {
      name: "Starter",
      price: "$29",
      period: "per month",
      description: "Perfect for individual creators and small teams",
      features: [
        "Up to 50 video uploads",
        "Basic AI transcription",
        "Speaker identification",
        "Object detection",
        "Standard search capabilities",
        "Email support"
      ],
      popular: false
    },
    {
      name: "Professional",
      price: "$99",
      period: "per month",
      description: "Ideal for growing businesses and content teams",
      features: [
        "Up to 500 video uploads",
        "Advanced AI transcription",
        "Multi-speaker diarization",
        "Advanced object detection",
        "Semantic search with LLM",
        "Custom clip generation",
        "API access",
        "Priority support"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "contact us",
      description: "For large organizations with custom requirements",
      features: [
        "Unlimited video uploads",
        "Custom AI model training",
        "Advanced analytics dashboard",
        "White-label solutions",
        "Dedicated account manager",
        "Custom integrations",
        "SLA guarantees",
        "24/7 phone support"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Can I change my plan at any time?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing adjustments."
    },
    {
      question: "What video formats do you support?",
      answer: "We support all major video formats including MP4, MOV, AVI, MKV, and more. Our AI can process videos up to 4K resolution and 4 hours in length."
    },
    {
      question: "How accurate is the AI transcription?",
      answer: "Our AI transcription achieves 95%+ accuracy for clear audio. We use advanced models that can handle multiple speakers, accents, and technical terminology."
    },
    {
      question: "Is my video content secure?",
      answer: "Absolutely. We use enterprise-grade encryption and security measures. Your videos are processed securely and never shared with third parties."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 30-day money-back guarantee. If you're not satisfied with our service, contact our support team for a full refund."
    },
    {
      question: "Can I export my processed data?",
      answer: "Yes, you can export transcripts, search results, and generated clips in various formats including JSON, CSV, and video files."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 text-white relative overflow-hidden">
      <DocumentTitle 
        title="Pricing - VideoBrain.ai | Affordable Video Intelligence Plans"
        description="Choose the perfect VideoBrain.ai plan for your needs. From individual creators to enterprise teams, we offer flexible pricing with advanced AI video analysis capabilities."
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-pink-600 flex items-center justify-center relative">
              <Brain className="h-6 w-6 text-white" />
              <div className="absolute inset-0 rounded-full bg-pink-600 animate-pulse opacity-50"></div>
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">VideoBrain</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-white font-semibold">Pricing</Link>
            <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/login">Get Started</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Parallax background elements */}
      <div ref={starsRef} className="absolute inset-0 pointer-events-none" style={starsStyle}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
        
        {/* Stars */}
        <div className="stars absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div 
              key={i} 
              className="absolute rounded-full bg-white" 
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${Math.random() * 5 + 3}s infinite ${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Floating planet */}
      <div 
        ref={planet1Ref}
        className="absolute -right-32 top-1/3 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/20 to-indigo-600/20 blur-xl"
        style={planet1Style}
      ></div>

      {/* Hero section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Choose the perfect plan for your video intelligence needs. Start free and scale as you grow.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 relative z-10">
        <div className="container px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl relative ${
                  plan.popular 
                    ? 'border-pink-500 shadow-lg shadow-pink-500/20 lg:scale-105' 
                    : 'border-white/10 hover:border-pink-500/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 lg:px-6 py-2 rounded-full text-xs lg:text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6 lg:mb-8">
                  <h3 className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4">{plan.name}</h3>
                  <div className="mb-3 lg:mb-4">
                    <span className="text-3xl lg:text-4xl font-extrabold">{plan.price}</span>
                    {plan.price !== "Free" && plan.price !== "Custom" && <span className="text-gray-400 ml-2 text-sm lg:text-base">{plan.period}</span>}
                    {plan.price === "Custom" && <span className="text-gray-400 ml-2 text-sm lg:text-base">{plan.period}</span>}
                  </div>
                  <p className="text-gray-300 text-xs lg:text-sm leading-relaxed">{plan.description}</p>
                </div>

                <div className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="h-4 lg:h-5 w-4 lg:w-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs lg:text-sm text-gray-200 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  size="lg" 
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-pink-600 hover:bg-pink-700' 
                      : 'bg-white/10 hover:bg-white/20 border border-white/20'
                  }`}
                  asChild
                >
                  <Link to={user ? "/dashboard" : "/login"}>
                    {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black/30 backdrop-blur-sm relative z-10">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Frequently Asked Questions
            </span>
          </h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-pink-500/30 transition-colors">
                <h3 className="text-lg font-semibold mb-3 text-white">{faq.question}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of creators transforming their video workflows with VideoBrain.
            </p>
            
            <Button size="lg" asChild className="bg-pink-600 hover:bg-pink-700 px-8 py-6 text-lg group transition-all duration-300">
              {user ? 
                <Link to="/dashboard" className="flex items-center">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link> : 
                <Link to="/login" className="flex items-center">
                  Start Free Today
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              }
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-md py-12 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="rounded-md bg-pink-600 p-1">
                <div className="h-6 w-6 text-white flex items-center justify-center font-bold">VB</div>
              </div>
              <span className="font-semibold text-xl">VideoBrain.ai</span>
            </div>
            <div className="flex gap-6">
              <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-white font-semibold">Pricing</Link>
              <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>
      {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}
      </style>
    </div>
  );
};

export default Pricing;
